import { useState } from 'react';

import { PublicEntityTemplate } from '@payaca/types/entity-templates';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';

import QuickActionsElement from '@payaca/components/quickActionsTableCell/QuickActionsElement';

import { UpdateServicePlanCommitmentInput } from '@payaca/store/servicePlans/servicePlansTypes';
import { Nullish } from '@payaca/utilities/types';
import CreateEditEntityTemplateModal from '../createEditEntityTemplateModal/CreateEditEntityTemplateModal';

import UntitledIcon from '@payaca/untitled-icons';
import { ControllableServicePlanCommitment } from './ServicePlanCommitmentsControl';

type Props = {
  commitment: ControllableServicePlanCommitment<'task' | 'scheduledEvent'>;
  onChange: (
    commitment: Omit<
      UpdateServicePlanCommitmentInput,
      'publicId' | 'accountId'
    > & {
      publicId?: Nullish<UpdateServicePlanCommitmentInput['publicId']>;
    }
  ) => Promise<void>;
  onRemove: () => Promise<void>;
};

const ServicePlanCommitmentControl = ({
  commitment,
  onChange,
  onRemove,
}: Props) => {
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);

  return (
    <div className="service-plan-commitment-control">
      {/* scheduled/event task commitment */}
      <CommitmentTemplateControl
        template={commitment.entityTemplate}
        onChange={(template) => {
          return onChange({
            ...commitment,
            scalesWithQuantity: commitment.scalesWithQuantity ?? true,
            entityTemplate: template,
          });
        }}
        onRemove={() => {
          return onRemove();
        }}
      />

      {/* scheduled event linked tasks */}
      {commitment.entityTemplate.entityType === 'scheduledEvent' && (
        <div className="event-tasks-wrapper">
          {(
            commitment as ControllableServicePlanCommitment<'scheduledEvent'>
          ).entityTemplate.data?.tasks?.map(
            (
              task: Omit<PublicEntityTemplate<'task'>, 'publicId'>,
              index: number
            ) => {
              return (
                <CommitmentTemplateControl
                  key={`commitment-template-control-${index}`}
                  template={task}
                  isChildOfScheduledEvent
                  onChange={async (updatedTask) => {
                    const tasks = [
                      ...((
                        commitment as ControllableServicePlanCommitment<'scheduledEvent'>
                      ).entityTemplate.data?.tasks || []),
                    ];
                    // @ts-expect-error
                    tasks[index] = updatedTask;

                    return onChange({
                      ...commitment,
                      scalesWithQuantity: commitment.scalesWithQuantity ?? true,
                      entityTemplate: {
                        entityType: commitment.entityTemplate.entityType,
                        data: {
                          ...commitment.entityTemplate.data,
                          tasks: tasks,
                        },
                      },
                    });
                  }}
                  onRemove={async () => {
                    const tasks = [
                      ...((
                        commitment as ControllableServicePlanCommitment<'scheduledEvent'>
                      ).entityTemplate.data?.tasks || []),
                    ];

                    tasks.splice(index, 1);

                    return onChange({
                      ...commitment,
                      scalesWithQuantity: commitment.scalesWithQuantity ?? true,
                      entityTemplate: {
                        entityType: commitment.entityTemplate.entityType,
                        data: {
                          ...commitment.entityTemplate.data,
                          tasks: tasks,
                        },
                      },
                    });
                  }}
                />
              );
            }
          )}
          <div>
            <Button
              styleVariant={ButtonStyleVariant.ANCHOR}
              onClick={() => {
                setShowAddTaskModal(true);
              }}
            >
              Add linked Task
            </Button>
            <CreateEditEntityTemplateModal
              isOpen={showAddTaskModal}
              onClose={() => setShowAddTaskModal(false)}
              action="Create"
              isChildOfScheduledEvent
              template={{
                entityType: 'task',
                data: {},
              }}
              onRequestSave={(taskTemplate) => {
                const entityTemplate = commitment.entityTemplate;
                setShowAddTaskModal(false);
                return onChange({
                  ...commitment,
                  scalesWithQuantity: commitment.scalesWithQuantity ?? true,
                  entityTemplate: {
                    entityType: entityTemplate.entityType,
                    data: {
                      ...commitment.entityTemplate.data,
                      tasks: [
                        // @ts-expect-error todo resolve this
                        ...(commitment?.entityTemplate?.data?.tasks || []),
                        taskTemplate,
                      ],
                    },
                  },
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicePlanCommitmentControl;

type CommitmentTemplateControlProps = {
  template: Omit<
    PublicEntityTemplate<'task' | 'scheduledEvent'>,
    'publicId' | 'accountId'
  >;
  isChildOfScheduledEvent?: boolean;
  onChange: (
    template: Omit<
      PublicEntityTemplate<'task' | 'scheduledEvent'>,
      'publicId' | 'accountId'
    >
  ) => Promise<void>;
  onRemove: () => Promise<void>;
};

const CommitmentTemplateControl = ({
  template,
  isChildOfScheduledEvent,
  onChange,
  onRemove,
}: CommitmentTemplateControlProps) => {
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const templateIcon =
    template.entityType === 'scheduledEvent' ? (
      <UntitledIcon className="size-4" name="calendar-check-02.3" />
    ) : (
      <UntitledIcon className="size-4" name="list.3" />
    );

  const [isRemoving, setIsRemoving] = useState(false);

  return (
    <div className="commitment-entity">
      {templateIcon}

      <div className="name-users-wrapper">
        {/* task/scheduled event name */}
        {template?.data?.name?.length ? (
          <strong>{template.data.name}</strong>
        ) : (
          <em>
            {template.entityType === 'scheduledEvent' ? 'Event' : 'Task'}{' '}
            template
          </em>
        )}
      </div>

      {/* quick actions */}
      <QuickActionsElement
        recordId={''}
        quickActions={[
          {
            actionName: 'Edit',
            actionBehaviour: () => {
              setShowEditTemplateModal(true);
            },
          },
          {
            actionName: 'Delete',
            actionBehaviour: () => {
              setIsRemoving(true);
              // oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
              onRemove().then(() => {
                setIsRemoving(false);
              });
            },
            isActionProcessing: isRemoving,
          },
        ]}
        renderDisabledIfNoActions={true}
      />

      <CreateEditEntityTemplateModal
        isOpen={showEditTemplateModal}
        onClose={() => setShowEditTemplateModal(false)}
        action="Edit"
        isChildOfScheduledEvent={isChildOfScheduledEvent}
        template={template}
        onRequestSave={(t) => {
          setShowEditTemplateModal(false);
          // oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
          void onChange(
            t as Omit<
              PublicEntityTemplate<'task' | 'scheduledEvent'>,
              'publicId' | 'accountId'
            >
          );
        }}
        allowModifyTemplateNameDescription={false}
      />
    </div>
  );
};
